// src/context/OrgContext.js
import React from "react";
import api from "../api/client";
import { useAuth } from "./AuthContext";

const OrgContext = React.createContext(null);
export const useOrg = () => React.useContext(OrgContext);

export default function OrgProvider({ children }) {
  const { user } = useAuth();
  const [orgs, setOrgs] = React.useState([]);
  const [current, setCurrent] = React.useState(() => {
    const stored = JSON.parse(localStorage.getItem("org") || "{}");
    return stored.current || process.env.REACT_APP_ORG_DEFAULT || "default";
  });

  // Persist + make axios interceptor pick it up on next request
  const setOrg = (o) => {
    setCurrent(o);
    localStorage.setItem("org", JSON.stringify({ current: o, list: orgs }));
    // let any listeners (or other tabs) react immediately
    window.dispatchEvent(new Event("org-changed"));
  };

  const refreshOrgs = React.useCallback(async () => {
    try {
      let arr = [];
      if (user?.currentRole === "Super Admin") {
        const r = await api.get("/orgs/list");
        // super admin can be more tightnened if needed:
        // const r = await api.get("/superadmin/orgs/list");
        //arr = (r.data?.orgs || []).map(o => o.slug);
        arr = Array.isArray(r.data) ? r.data : (r.data?.orgs || []);
      } else if (user?.currentRole === "Vendor") {
        const r = await api.get("/orgs/for_user", { params: { uid: user.uid } });
        arr = Array.isArray(r.data) ? r.data : (r.data?.orgs || []);
      } else {
        const stored = JSON.parse(localStorage.getItem("org") || "{}");
        arr = stored.list && stored.list.length ? stored.list : [current];
      }
      arr = Array.from(new Set(arr)).sort((a,b)=>a.localeCompare(b));
      if (!arr.includes(current)) setCurrent(arr[0] || "default");
      setOrgs(arr);
      localStorage.setItem("org", JSON.stringify({ current: arr.includes(current) ? current : (arr[0] || "default"), list: arr }));
    } catch {
      const stored = JSON.parse(localStorage.getItem("org") || "{}");
      const arr = stored.list && stored.list.length ? stored.list : [current];
      setOrgs(arr);
    }
  }, [user?.currentRole, user?.uid, current]);

  // Try to fetch available orgs (graceful fallback to current-only).
  React.useEffect(() => {
    if (user?.currentRole === "Super Admin" || user?.currentRole === "Vendor") {
      refreshOrgs();
    } else {
      setOrgs([current]);
    }
  }, [user?.currentRole, refreshOrgs, current]);

  React.useEffect(() => {
    const sync = () => {
      const stored = JSON.parse(localStorage.getItem("org") || "{}");
      if (stored.current && stored.current !== current) {
        setCurrent(stored.current);
        setOrgs(stored.list && stored.list.length ? stored.list : [stored.current]);
      }
    };
    window.addEventListener("storage", sync);
    window.addEventListener("org-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("org-changed", sync);
    };
  }, [current]);

  return (
    <OrgContext.Provider value={{ orgs, current, setOrg, refreshOrgs }}>
      {children}
    </OrgContext.Provider>
  );
}
